import { type HttpContext } from '@angular/common/http';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { Stitch } from '@modules/common/types/stitch';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { Message } from '@modules/messages/types/message';
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { Project } from '@modules/tasks/types/project';
import { Task } from '@modules/tasks/types/task';
import { Notebook } from '@modules/notes/types/notebook';
import { Note } from '@modules/notes/types/note';
import { Group } from '@modules/contacts/types/group';
import { Contact } from '@modules/contacts/types/contact';
import { Folder } from '@modules/files/types/folder';
import { File } from '@modules/files/types/file';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { EventFormComponent } from '@modules/calendar-app/components/event-form/event-form.component';

@Component({
  selector: 'app-dynamic-panel-forms-container',
  templateUrl: './forms-container.component.html',
  styleUrls: ['./forms-container.component.less'],
  standalone: false,
})
export class FormsContainerComponent implements OnDestroy {
  public formItem: Stitch;
  public requestContext: HttpContext | undefined;
  public formType: StitchType;
  public StitchType = StitchType;
  public preSelectedTab: string;
  private readonly alive = new Subject<void>();

  @ViewChild('formRef', { static: false }) formComponent?: EventFormComponent;

  constructor(
    private readonly dpService: DynamicPanelService,
    private readonly formChangesModalsService: FormChangesModalsService,
  ) {
    this.dpService
      .getFormState()
      .pipe(takeUntil(this.alive))
      .subscribe(({ item, requestContext }) => {
        this.formItem = item;
        this.requestContext = requestContext;
        this.formType = item?.getStitchType?.();
      });

    this.dpService
      .getFormTab()
      .pipe(takeUntil(this.alive))
      .subscribe((tab) => (this.preSelectedTab = tab));
  }

  get openedFromHub(): boolean {
    return this.dpService.openedFromHub();
  }

  public handleCancel(shouldClose = true): void {
    this.formChangesModalsService.confirmUnsavedChanges(this.formItem.id).subscribe((canLeave) => {
      if (canLeave && shouldClose) {
        this.dpService.setFormItem(null);
        this.dpService.setFormTab(null);
      }
    });
  }

  public handleBack(): void {
    this.formChangesModalsService.confirmUnsavedChanges(this.formItem?.id).subscribe((canLeave) => {
      if (canLeave) {
        this.dpService.goBack();
      }
    });
  }

  // Typed getters for form items
  get messageFolder(): MessageFolder {
    return this.formItem as MessageFolder;
  }

  get message(): Message {
    return this.formItem as Message;
  }

  get calendar(): Calendar {
    return this.formItem as Calendar;
  }

  get event(): CalendarEvent {
    return this.formItem as CalendarEvent;
  }

  get project(): Project {
    return this.formItem as Project;
  }

  get task(): Task {
    return this.formItem as Task;
  }

  get notebook(): Notebook {
    return this.formItem as Notebook;
  }

  get note(): Note {
    return this.formItem as Note;
  }

  get group(): Group {
    return this.formItem as Group;
  }

  get contact(): Contact {
    return this.formItem as Contact;
  }

  get folder(): Folder {
    return this.formItem as Folder;
  }

  get file(): File {
    return this.formItem as File;
  }

  get sheet(): Sheet {
    return this.formItem as Sheet;
  }

  get sheetFolder(): SheetFolder {
    return this.formItem as SheetFolder;
  }

  public ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }
}
